import { useRetrieveIntraSquadMatch, useRetrievePlayer, useRetrieveTeam, } from "../ApiProvider";
import { useRetrieveDivision } from "../api/DivisionApiProvider";
import { useRetrieveLeague } from "../api/LeagueApiProvider";
import { useRetrieveExternalMatch } from "../api/MatchApiProvider";
import { useRetrieveLeagueMatch, useRetrieveSeason, } from "../api/SeasonApiProvider";
import { useRetrieveIntraSquadTournament, useRetrieveIntraSquadTournamentMatch, } from "../api/TournamentApiProvider";
import { useRetrieveTransferRequest } from "../api/TransferRequestApiProvider";
import { useRetrieveVenue } from "../api/VenueApiProvider";
import { playerDisplayName } from "../players/PlayerDisplayNameLink";
import { EntityResolver } from "./routeConfig";
/**
 * Formats an IntraSquadMatch for display in breadcrumbs.
 */
var formatIntraSquadMatch = function (match) {
    if (!match)
        return undefined;
    return "".concat(match.teamAName, " vs ").concat(match.teamBName);
};
/**
 * Formats an ExternalMatch for display in breadcrumbs.
 */
var formatExternalMatch = function (match) {
    if (!match)
        return undefined;
    return "vs ".concat(match.opponentTeam.name);
};
/**
 * Formats a LeagueMatch for display in breadcrumbs.
 */
var formatLeagueMatch = function (match) {
    if (!match)
        return undefined;
    var home = match.homeTeam.name;
    var away = match.awayTeam.name;
    return "".concat(home, " v ").concat(away);
};
/**
 * Hook that resolves entity names for breadcrumb display.
 * All hooks are called unconditionally (React rules) but only enabled when needed.
 */
export var useBreadcrumbData = function (segments) {
    var _a;
    var _b, _c, _d, _e, _f, _g, _h, _j, _k, _l, _m, _o, _p, _q, _r, _s, _t, _u;
    // Extract entity IDs from segments
    var teamId = (_b = segments.find(function (s) { return s.resolver === EntityResolver.Team; })) === null || _b === void 0 ? void 0 : _b.value;
    var leagueId = (_c = segments.find(function (s) { return s.resolver === EntityResolver.League; })) === null || _c === void 0 ? void 0 : _c.value;
    var venueId = (_d = segments.find(function (s) { return s.resolver === EntityResolver.Venue; })) === null || _d === void 0 ? void 0 : _d.value;
    var playerId = (_e = segments.find(function (s) { return s.resolver === EntityResolver.Player; })) === null || _e === void 0 ? void 0 : _e.value;
    var seasonId = (_f = segments.find(function (s) { return s.resolver === EntityResolver.Season; })) === null || _f === void 0 ? void 0 : _f.value;
    var divisionId = (_g = segments.find(function (s) { return s.resolver === EntityResolver.Division; })) === null || _g === void 0 ? void 0 : _g.value;
    var intraSquadMatchId = (_h = segments.find(function (s) { return s.resolver === EntityResolver.IntraSquadMatch; })) === null || _h === void 0 ? void 0 : _h.value;
    var externalMatchId = (_j = segments.find(function (s) { return s.resolver === EntityResolver.ExternalMatch; })) === null || _j === void 0 ? void 0 : _j.value;
    var intraSquadTournamentId = (_k = segments.find(function (s) { return s.resolver === EntityResolver.IntraSquadTournament; })) === null || _k === void 0 ? void 0 : _k.value;
    var intraSquadTournamentMatchId = (_l = segments.find(function (s) { return s.resolver === EntityResolver.IntraSquadTournamentMatch; })) === null || _l === void 0 ? void 0 : _l.value;
    var transferId = (_m = segments.find(function (s) { return s.resolver === EntityResolver.Transfer; })) === null || _m === void 0 ? void 0 : _m.value;
    var leagueMatchId = (_o = segments.find(function (s) { return s.resolver === EntityResolver.LeagueMatch; })) === null || _o === void 0 ? void 0 : _o.value;
    // Call all hooks unconditionally, but enable only what's needed
    var team = useRetrieveTeam({ id: teamId, enabled: !!teamId });
    var league = useRetrieveLeague({ id: leagueId, enabled: !!leagueId });
    var venue = useRetrieveVenue({ id: venueId, enabled: !!venueId });
    var player = useRetrievePlayer({ id: playerId, enabled: !!playerId });
    var season = useRetrieveSeason({ id: seasonId, enabled: !!seasonId });
    var division = useRetrieveDivision({
        id: divisionId,
        enabled: !!divisionId,
    });
    var intraSquadMatch = useRetrieveIntraSquadMatch({
        id: intraSquadMatchId,
        enabled: !!intraSquadMatchId,
    });
    var externalMatch = useRetrieveExternalMatch({
        id: externalMatchId,
        enabled: !!externalMatchId,
    });
    var intraSquadTournament = useRetrieveIntraSquadTournament({
        id: intraSquadTournamentId,
        enabled: !!intraSquadTournamentId,
    });
    var intraSquadTournamentMatch = useRetrieveIntraSquadTournamentMatch({
        id: intraSquadTournamentMatchId,
        enabled: !!intraSquadTournamentMatchId,
    });
    var transfer = useRetrieveTransferRequest({ id: transferId });
    var leagueMatch = useRetrieveLeagueMatch({
        id: leagueMatchId,
        enabled: !!leagueMatchId,
    });
    // Determine if any enabled query is loading
    var isLoading = (!!teamId && team.isLoading) ||
        (!!leagueId && league.isLoading) ||
        (!!venueId && venue.isLoading) ||
        (!!playerId && player.isLoading) ||
        (!!seasonId && season.isLoading) ||
        (!!divisionId && division.isLoading) ||
        (!!intraSquadMatchId && intraSquadMatch.isLoading) ||
        (!!externalMatchId && externalMatch.isLoading) ||
        (!!intraSquadTournamentId && intraSquadTournament.isLoading) ||
        (!!intraSquadTournamentMatchId && intraSquadTournamentMatch.isLoading) ||
        (!!transferId && transfer.isLoading) ||
        (!!leagueMatchId && leagueMatch.isLoading);
    return _a = {},
        _a[EntityResolver.Team] = (_p = team.data) === null || _p === void 0 ? void 0 : _p.name,
        _a[EntityResolver.League] = (_q = league.data) === null || _q === void 0 ? void 0 : _q.name,
        _a[EntityResolver.Venue] = (_r = venue.data) === null || _r === void 0 ? void 0 : _r.name,
        _a[EntityResolver.Player] = playerDisplayName(player.data),
        _a[EntityResolver.Season] = (_s = season.data) === null || _s === void 0 ? void 0 : _s.name,
        _a[EntityResolver.Division] = (_t = division.data) === null || _t === void 0 ? void 0 : _t.name,
        _a[EntityResolver.IntraSquadMatch] = formatIntraSquadMatch(intraSquadMatch.data),
        _a[EntityResolver.ExternalMatch] = formatExternalMatch(externalMatch.data),
        _a[EntityResolver.IntraSquadTournament] = ((_u = intraSquadTournament.data) === null || _u === void 0 ? void 0 : _u.tournamentName) || "Tournament",
        _a[EntityResolver.IntraSquadTournamentMatch] = intraSquadTournamentMatch.data
            ? "Round ".concat(intraSquadTournamentMatch.data.round)
            : undefined,
        _a[EntityResolver.Transfer] = transfer.data
            ? "Transfer #".concat(transfer.data.id)
            : undefined,
        _a[EntityResolver.LeagueMatch] = formatLeagueMatch(leagueMatch.data),
        _a.isLoading = isLoading,
        _a;
};
